/**
 * The loyalty club's e-mails and public answer headers (club A, spec §8) —
 * pure, so the route (app/api/club/route.ts) only sends what is tested here.
 *
 * Every mail names the shop and is Georgian (documents stay Georgian whatever
 * the device speaks). The code never goes in a subject: a subject shows on a
 * locked phone. No output carries a recipient — the route hands the address
 * to Resend separately and never logs it.
 */

export type CodePurpose = 'register' | 'recover' | 'link' | 'replace'
export type Mail = { subject: string; html: string; text: string }

const esc = (v: string) =>
  v.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;')

const WHY: Record<CodePurpose, string> = {
  register: 'კლუბში რეგისტრაციის დასასრულებლად',
  recover: 'თქვენი კლუბის ბარათის ბმულის მისაღებად',
  link: 'სალაროში კლუბის ბარათის მისაღებად — უთხარით კოდი მოლარეს',
  replace: 'კლუბის ბარათის შესაცვლელად (კოდის დადასტურების შემდეგ ძველი ბარათი აღარ იმუშავებს)',
}

const FOOT = 'თუ ეს თქვენ არ გაგიკეთებიათ, უბრალოდ წაშალეთ ეს წერილი.'

export function codeMail(orgName: string, code: string, purpose: CodePurpose): Mail {
  const subject = `${orgName} — კლუბის კოდი`
  const text = `${orgName}\n\n${WHY[purpose]} შეიყვანეთ კოდი: ${code}\n\nკოდი მოქმედებს 10 წუთი.\n${FOOT}`
  const html = `<p><strong>${esc(orgName)}</strong></p>
<p>${WHY[purpose]} შეიყვანეთ კოდი:</p>
<p style="font-size:28px;letter-spacing:6px;font-weight:700">${code}</p>
<p>კოდი მოქმედებს 10 წუთი.<br>${FOOT}</p>`
  return { subject, html, text }
}

export function cardMail(orgName: string, firstName: string, cardUrl: string): Mail {
  const subject = `${orgName} — თქვენი კლუბის ბარათი`
  const hello = firstName ? `${firstName}, ` : ''
  const text = `${orgName}\n\n${hello}თქვენი კლუბის ბარათი აქ არის:\n${cardUrl}\n\n`
    + 'სალაროში აჩვენეთ ბარათის QR კოდი. შეინახეთ ეს ბმული — ვინც მას ფლობს, ბარათითაც ისარგებლებს.\n'
    + 'ბარათი დაკარგეთ? გახსენით ბმული და დააჭირეთ „ბარათის შეცვლა".'
  const html = `<p><strong>${esc(orgName)}</strong></p>
<p>${esc(hello)}თქვენი კლუბის ბარათი აქ არის:</p>
<p><a href="${esc(cardUrl)}" style="font-size:18px;font-weight:700">ბარათის გახსნა</a></p>
<p>სალაროში აჩვენეთ ბარათის QR კოდი. შეინახეთ ეს ბმული — ვინც მას ფლობს, ბარათითაც ისარგებლებს.<br>
ბარათი დაკარგეთ? გახსენით ბმული და დააჭირეთ „ბარათის შეცვლა".</p>`
  return { subject, html, text }
}

/** Every public club answer: never cached, never sent on as a referrer, never indexed (Codex #I2). */
export function publicHeaders(): Record<string, string> {
  return { 'Cache-Control': 'private, no-store', 'Referrer-Policy': 'no-referrer', 'X-Robots-Tag': 'noindex' }
}

/** The database's own refusal code (CLUB_* and the few shared ones), or null for anything else. */
export function clubRefusal(message: string | null | undefined): string | null {
  const m = /\b(CLUB_[A-Z_]+|CUSTOMER_NOT_FOUND|ORG_INACTIVE)\b/.exec(message ?? '')
  return m ? m[1] : null
}
